import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import Highcharts from 'highcharts';
import { Observable, Subject, Subscription, catchError, distinctUntilChanged, filter, forkJoin, map, of, switchMap } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';

import { StructureService } from '../../core/services/structure/structure.service';
import { StructureLabelsService } from '../../core/services/structure/structure-labels.service';
import { MetricConfig, ProjectStructureLevel } from '../../core/models/project-structure.model';
import { ContentChartService, KnownMetricType, SupportedMetricType } from '../../core/services/chart/content-chart.service';
import { ChartService } from '../../core/services/chart/chart.service';
import { TIME_SERIES_CHART_CONFIG } from '../../core/models/time-series-chart-config.model';
import { ProjectStructureEntity } from '../../core/models/project-structure-entities.model';
import { normalizeLanguageTag } from '../../core/utils/language';
import { ProjectContextService } from '../../core/services/project-context/project-context.service';
import { isSystemContentType, SYSTEM_CONTENT_TYPE_METRICS, APP_STARTUP_FUNNEL_STAGES } from '../../core/constants/system-content-types.constant';

type ContentPathNode = { contentTypeId: string; entityId: string };

const RESERVED_ROUTES = new Set([
  'home',
  'login',
  'charts',
  'users',
  'devices',
  'legal-disclaimer',
  'cookie-policy'
]);

interface HierarchyMenuLevel {
  menuIndex: number; // unique index in rendered sidebar
  depth: number; // hierarchy depth (0 = root)
  contentTypeId: string;
  parentSelectedId: string | null;
  selectedId: string; // 'ALL' or concrete id
  routeNodes: ContentPathNode[]; // prefix + this level (used to build navigation URLs)
  expanded: boolean;
  entities: ProjectStructureEntity[];
  loading: boolean;
}

@Component({
  selector: 'app-charts',
  templateUrl: './charts.component.html',
  styleUrl: './charts.component.scss'
})
export class ChartsComponent implements OnInit, OnDestroy {
  chartConfig: Highcharts.Options | undefined;
  loading = false;
  error: string | null = null;
  ready = false;

  currentContentTypeId = 'itinerariesGroups';
  currentEntityId = 'ALL';

  metricTypes: SupportedMetricType[] = [];
  selectedMetricType: SupportedMetricType | null = null;
  subtypesForMetric: string[] = [];
  selectedSubtype: string | null = null;

  hierarchyMenu: HierarchyMenuLevel[] = [];
  expandedHierarchyIndex: number | null = null;
  isCurrentSystemType = false;
  isFunnelMode = false;
  funnelConversions: Array<{ label: string; rate: string }> = [];

  private projectId: string | null = null;
  private readonly subscriptions = new Subscription();
  private readonly reloadChart$ = new Subject<ContentPathNode[] | undefined>();
  private structureLevels: ProjectStructureLevel[] = [];
  private currentStructureMetrics: Record<string, MetricConfig> = {};
  private seriesMeta: Array<{ id: string; name: string }> = [];
  private entitiesByType = new Map<string, ProjectStructureEntity[]>();
  private entitiesLoadingByType = new Set<string>();
  private lastNodes: ContentPathNode[] = [];

  constructor(
    private readonly router: Router,
    private readonly translocoSrv: TranslocoService,
    private readonly structureService: StructureService,
    private readonly structureLabelsService: StructureLabelsService,
    private readonly contentChartService: ContentChartService,
    private readonly chartService: ChartService,
    private readonly projectContext: ProjectContextService
  ) {}

  ngOnInit(): void {
    this.setupReloadChartPipeline();

    this.subscriptions.add(
      this.router.events
        .pipe(filter((evt) => evt instanceof NavigationEnd))
        .subscribe(() => this.syncFromUrl())
    );

    this.subscriptions.add(
      this.translocoSrv.langChanges$.subscribe((lang) => {
        // Language affects server-side translated entity names (and local labels),
        // so we reload the chart rather than rebuilding with stale series names.
        this.entitiesByType.clear();
        this.entitiesLoadingByType.clear();
        this.expandedHierarchyIndex = null;
        // Force a refetch for entities (and any other language-scoped caches).
        if (this.projectId) {
          this.structureService.clearEntitiesCache(this.projectId);
        }
        this.reloadChart();
        // `lang` is the new language. Sync after caches are cleared.
        // (We still normalize via `activeLanguage()` for consistency.)
        this.syncFromUrl(true);
      })
    );

    this.subscriptions.add(
      this.projectContext.selectedProjectId$
        .pipe(
          filter((id): id is string => typeof id === 'string' && id.trim().length > 0),
          distinctUntilChanged(),
          switchMap((projectId) => {
            this.projectId = projectId;

            this.seriesMeta = [];
            this.entitiesByType.clear();
            this.entitiesLoadingByType.clear();
            this.expandedHierarchyIndex = null;
            this.hierarchyMenu = [];
            this.structureLevels = [];

            this.structureService.clearCache();

            return this.structureService.getProjectStructure(projectId).pipe(
              catchError(() => of(null))
            );
          })
        )
        .subscribe((structure) => {
          this.structureLevels = structure?.levels || [];
          this.currentStructureMetrics = structure?.metrics || {};
          this.syncFromUrl(true);
        })
    );
  }

  // Child content type selection is driven by the left hierarchy menu now.
  // (The old top-row child-type buttons were removed to avoid duplicating metric buttons.)

  selectMetric(metricType: SupportedMetricType): void {
    if (!this.metricTypes.includes(metricType)) {
      return;
    }
    this.selectedMetricType = metricType;
    this.updateSubtypesForMetric(metricType);
    this.isFunnelMode = metricType === 'app_startup';
    if (this.isFunnelMode) {
      this.funnelConversions = [];
    }
    this.reloadChart();
  }

  selectSubtype(subtype: string): void {
    if (!this.subtypesForMetric.includes(subtype)) {
      return;
    }
    this.selectedSubtype = subtype;
    this.reloadChart();
  }

  getSubtypeLabel(subtype: string): string {
    if (!this.selectedMetricType) {
      return subtype;
    }
    return this.structureLabelsService.getSubtypeLabel(this.selectedMetricType, subtype, this.translocoSrv.getActiveLang());
  }

  getStructureTypeLabel(contentTypeId: string): string {
    return this.structureLabelsService.getLabel(contentTypeId, this.translocoSrv.getActiveLang());
  }

  getMetricLabel(metricType: SupportedMetricType): string {
    return this.structureLabelsService.getLabel(metricType, this.translocoSrv.getActiveLang());
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private syncFromUrl(forceReload: boolean = false): void {
    const urlPath = this.router.url.split('?')[0].replace(/^\/+|\/+$/g, '');

    if (!urlPath || urlPath === 'charts') {
      // Entry point: redirect to first top-level type from structure.
      if (this.structureLevels.length > 0) {
        const top = this.extractTopLevelTypes(this.structureLevels)[0];
        if (top) {
          this.router.navigate([`/${top}`]);
          return;
        }
      }
      this.ready = true;
      return;
    }

    const segments = urlPath.split('/').filter(Boolean);
    if (segments.length > 0 && RESERVED_ROUTES.has(segments[0])) {
      this.ready = true;
      return;
    }

    const previousKey = this.viewKey();
    const nodes = this.parseNodes(segments);
    if (!nodes) {
      this.error = 'Ruta inválida (debe ser contentTypeId[/entityId/...]).';
      this.ready = true;
      return;
    }
    this.lastNodes = nodes;

    const chainError = this.validateStructureChain(nodes);
    if (chainError) {
      this.error = chainError;
      this.ready = true;
      return;
    }

    const leaf = nodes[nodes.length - 1];
    this.currentContentTypeId = leaf.contentTypeId;
    this.currentEntityId = leaf.entityId;
    this.isCurrentSystemType = isSystemContentType(this.currentContentTypeId);

    this.metricTypes = this.getSupportedMetricsForContentType(this.currentContentTypeId);
    if (this.metricTypes.length === 0) {
      this.selectedMetricType = null;
    } else if (!this.selectedMetricType || !this.metricTypes.includes(this.selectedMetricType)) {
      this.selectedMetricType = this.metricTypes[0];
    }
    if (this.selectedMetricType) {
      this.updateSubtypesForMetric(this.selectedMetricType);
    }
    this.isFunnelMode = this.selectedMetricType === 'app_startup';
    if (this.isFunnelMode) {
      this.funnelConversions = [];
    }

    this.rebuildHierarchyMenu(nodes);
    this.ready = true;

    if (forceReload || previousKey !== this.viewKey()) {
      this.reloadChart(nodes);
    }
  }

  private viewKey(): string {
    return [
      this.currentContentTypeId,
      this.currentEntityId,
      this.selectedMetricType || ''
    ].join('|');
  }

  private reloadChart(nodes?: ContentPathNode[]): void {
    this.reloadChart$.next(nodes);
  }

  private setupReloadChartPipeline(): void {
    this.subscriptions.add(
      this.reloadChart$.pipe(
        switchMap((nodes) => {
          this.loading = true;
          this.error = null;

          if (!this.projectId || !this.selectedMetricType || !this.selectedSubtype) {
            this.chartConfig = undefined;
            this.loading = false;
            return [];
          }

          const projectId = this.projectId;

          if (this.isFunnelMode) {
            return this.loadFunnelData(projectId);
          }

          const urlPath = this.router.url.split('?')[0].replace(/^\/+|\/+$/g, '');
          const segments = urlPath.split('/').filter(Boolean);
          const pathNodes = nodes || this.parseNodes(segments) || [];

          const scope = this.resolveChartScope(segments, pathNodes);
          if (!scope) {
            this.chartConfig = undefined;
            this.loading = false;
            return [];
          }

          const metricType = this.selectedMetricType!;
          const metricSubtype = this.selectedSubtype!;

          return this.contentChartService
            .loadSeries({
              projectId,
              sourceContentType: scope.sourceContentType,
              targetContentType: scope.targetContentType,
              contentId: scope.contentId,
              aggregationLevel: scope.aggregationLevel,
              metricType,
              metricSubtype
            })
            .pipe(
              map((entries) => ({ kind: 'series' as const, entries, pathNodes }))
            );
        })
      ).subscribe({
        next: (result) => {
          if (!result) {
            return;
          }
          if (result.kind === 'funnel') {
            this.seriesMeta = [];
            this.funnelConversions = result.conversions;
            this.chartConfig = result.config;
            this.loading = false;
            return;
          }
          const { entries, pathNodes } = result;
          const nonEmpty = entries.filter((entry) => entry.points.length > 0);
          this.seriesMeta = nonEmpty.map((entry) => ({ id: entry.id, name: entry.name }));
          this.chartConfig = this.buildChartConfig(nonEmpty, pathNodes);
          this.loading = false;
        },
        error: (err) => {
          this.seriesMeta = [];
          this.funnelConversions = [];
          this.chartConfig = this.buildChartConfig([], []);
          this.error = err instanceof Error ? err.message : 'Error cargando estadísticas';
          this.loading = false;
        }
      })
    );
  }

  private loadFunnelData(projectId: string): Observable<{
    kind: 'funnel';
    config: Highcharts.Options;
    conversions: Array<{ label: string; rate: string }>;
  }> {
    const queries: Record<string, Observable<Array<{ points: Array<[number, number]> }>>> = {};

    for (const stage of APP_STARTUP_FUNNEL_STAGES) {
      for (const subtype of stage.subtypes) {
        queries[`${stage.key}|${subtype}`] = this.contentChartService.loadSeries({
          projectId,
          sourceContentType: 'screens',
          targetContentType: 'screens',
          contentId: 'ALL',
          aggregationLevel: 'children',
          metricType: 'app_startup',
          metricSubtype: subtype
        });
      }
    }

    return forkJoin(queries).pipe(
      map((results) => {
        const stageTotals = APP_STARTUP_FUNNEL_STAGES.map((stage) => {
          let total = 0;
          for (const subtype of stage.subtypes) {
            const entries = results[`${stage.key}|${subtype}`] || [];
            for (const entry of entries) {
              total += entry.points.reduce((sum, pt) => sum + pt[1], 0);
            }
          }
          return { key: stage.key, total };
        });

        return {
          kind: 'funnel' as const,
          config: this.buildFunnelChartConfig(stageTotals),
          conversions: this.computeFunnelConversions(stageTotals)
        };
      }),
      catchError(() => {
        return of({
          kind: 'funnel' as const,
          config: this.buildFunnelChartConfig([]),
          conversions: []
        });
      })
    );
  }

  private buildFunnelChartConfig(data: Array<{ key: string; total: number }>): Highcharts.Options {
    const lang = this.translocoSrv.getActiveLang();
    const categories = data.map((d) =>
      this.structureLabelsService.getSubtypeLabel('app_startup', d.key, lang)
    );
    const values = data.map((d) => d.total);
    const title = this.structureLabelsService.getLabel('app_startup', lang);
    const seriesName = this.translocoSrv.translate('common.funnel-count');

    return {
      chart: { type: 'bar' },
      title: { text: title },
      xAxis: {
        categories,
        title: { text: null as unknown as string }
      },
      yAxis: {
        min: 0,
        title: { text: seriesName }
      },
      legend: { enabled: false },
      plotOptions: {
        bar: {
          dataLabels: { enabled: true }
        }
      },
      series: [{
        name: seriesName,
        type: 'bar' as const,
        data: values
      }]
    };
  }

  private computeFunnelConversions(data: Array<{ key: string; total: number }>): Array<{ label: string; rate: string }> {
    if (data.length < 2) {
      return [];
    }

    const lang = this.translocoSrv.getActiveLang();
    const labelFor = (key: string) => this.structureLabelsService.getSubtypeLabel('app_startup', key, lang);

    // Use the first stage (app_start) as denominator for all rates.
    // Step-by-step rates are misleading when later stages can exceed earlier ones
    // (e.g. users with pre-granted permissions skip permisos_entrada but still reach menu_principal).
    const base = data[0];
    const conversions: Array<{ label: string; rate: string }> = [];

    for (let i = 1; i < data.length; i++) {
      const stage = data[i];
      const rateNum = base.total > 0 ? (stage.total / base.total) * 100 : 0;
      if (rateNum > 100) {
        // Skip rates > 100%: they indicate stages counted across multiple paths,
        // not a valid funnel step relative to the base.
        continue;
      }
      const rate = rateNum.toFixed(1);
      conversions.push({
        label: this.translocoSrv.translate('common.funnel-conversion', {
          from: labelFor(base.key),
          to: labelFor(stage.key),
          rate
        }),
        rate
      });
    }

    const last = data[data.length - 1];
    const overallRateNum = base.total > 0 ? (last.total / base.total) * 100 : 0;
    const overallRate = overallRateNum.toFixed(1);
    conversions.push({
      label: this.translocoSrv.translate('common.funnel-overall', { rate: overallRate }),
      rate: overallRate
    });

    return conversions;
  }

  private buildChartConfig(entries: Array<{ id: string; name: string; points: Array<[number, number]> }>, nodes?: ContentPathNode[], titleKey?: string): Highcharts.Options {
    const year = this.chartService.yearSelected;
    const month = this.chartService.monthSelected;
    const monthText = this.translocoSrv.translate(`date.${Math.max(0, month - 1)}`).toLowerCase();

    const canNavigateDeeper = this.currentEntityId === 'ALL' && !this.isCurrentSystemType;

    const series = entries
      .map((entry) => ({
        name: entry.name,
        type: 'line' as const,
        data: entry.points
      }));

    const title = titleKey
      ? this.structureLabelsService.getLabel(titleKey, this.translocoSrv.getActiveLang())
      : this.getStructureTypeLabel(this.currentContentTypeId);

    return {
      chart: {
        type: TIME_SERIES_CHART_CONFIG.type
      },
      title: { text: title },
      plotOptions: {
        // Highcharts (in some builds) expects `point.options.events` to be an object;
        // avoid setting it to `undefined` which breaks hover handling.
        series: {
          cursor: canNavigateDeeper ? 'pointer' : 'default',
          point: {
            events: canNavigateDeeper
              ? {
                  click: (event: any) => {
                    const index = event?.point?.series?.index;
                    this.onSeriesClicked(index, nodes);
                  }
                }
              : {}
          }
        }
      },
      yAxis: { title: { text: titleKey ? this.structureLabelsService.getLabel(titleKey, this.translocoSrv.getActiveLang()) : (this.selectedMetricType ? this.getMetricLabel(this.selectedMetricType) : '') } },
      xAxis: {
        type: TIME_SERIES_CHART_CONFIG.xAxis.type as Highcharts.AxisTypeValue | undefined,
        title: {
          text: this.translocoSrv.translate('chart.label-x', { month: monthText, year }),
          align: 'high'
        },
        dateTimeLabelFormats: {
          day: TIME_SERIES_CHART_CONFIG.xAxis.dateTimeFormat.day
        }
      },
      legend: {
        align: 'left',
        verticalAlign: 'top',
        itemStyle: { fontSize: '14px' }
      },
      series
    };
  }

  private onSeriesClicked(index: number, nodes?: ContentPathNode[]): void {
    if (typeof index !== 'number' || index < 0 || index >= this.seriesMeta.length) {
      return;
    }

    const clicked = this.seriesMeta[index];
    const urlPath = this.router.url.split('?')[0].replace(/^\/+|\/+$/g, '');
    const segments = urlPath.split('/').filter(Boolean);
    const parsed = nodes || this.parseNodes(segments);
    if (!parsed) {
      return;
    }

    if (this.currentEntityId === 'ALL') {
      // Build the new path including the clicked entity.
      let nextSegments: string[];
      if (segments.length === 1) {
        nextSegments = [this.currentContentTypeId, clicked.id];
      } else {
        nextSegments = [...segments, clicked.id];
      }

      // If the clicked entity has children, append the first child type
      // so the chart immediately shows the children listing.
      const childTypes = this.getDirectChildTypes(this.currentContentTypeId);
      if (childTypes.length === 1) {
        nextSegments.push(childTypes[0]);
      }

      this.router.navigate([`/${nextSegments.join('/')}`]);
      return;
    }
  }

  private parseNodes(segments: string[]): ContentPathNode[] | null {
    if (segments.length === 1) {
      return [{ contentTypeId: segments[0], entityId: 'ALL' }];
    }

    if (segments.length % 2 === 0) {
      const nodes: ContentPathNode[] = [];
      for (let i = 0; i < segments.length; i += 2) {
        const contentTypeId = segments[i];
        const entityId = segments[i + 1];
        if (!contentTypeId || !entityId) {
          return null;
        }
        nodes.push({ contentTypeId, entityId });
      }

      return nodes;
    }

    // Trailing typeId means "ALL" at that level, scoped by its parent.
    if (segments.length >= 3) {
      const nodes: ContentPathNode[] = [];
      for (let i = 0; i < segments.length - 1; i += 2) {
        const contentTypeId = segments[i];
        const entityId = segments[i + 1];
        if (!contentTypeId || !entityId) {
          return null;
        }
        nodes.push({ contentTypeId, entityId });
      }
      const trailingTypeId = segments[segments.length - 1];
      if (!trailingTypeId) {
        return null;
      }
      nodes.push({ contentTypeId: trailingTypeId, entityId: 'ALL' });
      return nodes;
    }

    return null;
  }

  private resolveChartScope(
    urlSegments: string[],
    nodes: ContentPathNode[]
  ): {
    sourceContentType: string;
    targetContentType: string;
    contentId: string;
    aggregationLevel: 'children' | 'direct';
  } | null {
    if (!nodes || nodes.length === 0) {
      return null;
    }

    const leaf = nodes[nodes.length - 1];
    const leafIsAll = leaf.entityId === 'ALL';

    // Root listing: /<type>
    if (nodes.length === 1 && leafIsAll) {
      return {
        sourceContentType: leaf.contentTypeId,
        targetContentType: leaf.contentTypeId,
        contentId: 'ALL',
        aggregationLevel: 'children'
      };
    }

    // Scoped listing: /.../<parentType>/<parentId>/<childType>
    if (leafIsAll && nodes.length >= 2) {
      const parent = nodes[nodes.length - 2];
      return {
        sourceContentType: parent.contentTypeId,
        targetContentType: leaf.contentTypeId,
        contentId: parent.entityId,
        aggregationLevel: 'children'
      };
    }

    return {
      sourceContentType: leaf.contentTypeId,
      targetContentType: leaf.contentTypeId,
      contentId: leaf.entityId,
      aggregationLevel: 'direct'
    };
  }

  toggleHierarchyLevel(index: number): void {
    this.expandedHierarchyIndex = this.expandedHierarchyIndex === index ? null : index;
    this.hierarchyMenu = this.hierarchyMenu.map((entry) => ({ ...entry, expanded: entry.menuIndex === this.expandedHierarchyIndex }));
  }

  selectHierarchyEntity(menuIndex: number, entityId: string): void {
    this.expandedHierarchyIndex = null;
    this.hierarchyMenu = this.hierarchyMenu.map((entry) => ({ ...entry, expanded: false }));

    const selectedLevel = this.hierarchyMenu.find((lvl) => lvl.menuIndex === menuIndex);
    if (!selectedLevel || selectedLevel.routeNodes.length === 0) {
      return;
    }

    const last = selectedLevel.routeNodes[selectedLevel.routeNodes.length - 1];
    const nextNodes: ContentPathNode[] = [
      ...selectedLevel.routeNodes.slice(0, -1),
      { contentTypeId: last.contentTypeId, entityId }
    ];
    this.router.navigate([this.buildRouteFromNodes(nextNodes)]);
  }

  getMenuSelectedId(level: HierarchyMenuLevel): string {
    return level.selectedId || 'ALL';
  }

  getMenuSelectedName(level: HierarchyMenuLevel): string {
    const selectedId = this.getMenuSelectedId(level);
    if (selectedId === 'ALL') {
      return this.translocoSrv.translate('common.all');
    }

    const candidates = this.getEntitiesForLevel(level);
    const found = candidates.find((e) => e.id === selectedId);
    return found?.name || selectedId;
  }

  getEntitiesForLevel(level: HierarchyMenuLevel): ProjectStructureEntity[] {
    const all = level.entities || [];
    if (level.depth === 0) {
      return all.filter((e) => e.parentId === null);
    }
    if (!level.parentSelectedId) {
      return [];
    }
    return all.filter((e) => e.parentId === level.parentSelectedId);
  }

  private rebuildHierarchyMenu(nodes: ContentPathNode[]): void {
    if (!nodes || nodes.length === 0) {
      this.hierarchyMenu = [];
      return;
    }

    const firstAllIndex = nodes.findIndex((n) => n.entityId === 'ALL');
    const activeNodes = firstAllIndex >= 0 ? nodes.slice(0, firstAllIndex + 1) : nodes;

    // We show multiple child types only for the last displayed menu depth.
    // That "last depth" is the direct children of the deepest selected (non-ALL) node,
    // or, if the URL ends with an ALL-listing child node, the siblings of that child node.
    let prefixNodes: ContentPathNode[] = activeNodes;
    let branchParent: ContentPathNode | null = null;
    let selectedChildFromUrl: ContentPathNode | null = null;
    let branchChildTypes: string[] = [];

    const leaf = activeNodes[activeNodes.length - 1];
    if (leaf?.entityId === 'ALL') {
      const maybeParent = activeNodes.length >= 2 ? activeNodes[activeNodes.length - 2] : null;
      if (maybeParent && maybeParent.entityId !== 'ALL') {
        const childTypes = this.getDirectChildTypes(maybeParent.contentTypeId);
        if (childTypes.length > 0) {
          branchParent = maybeParent;
          branchChildTypes = childTypes;
          selectedChildFromUrl = leaf; // contentTypeId of the ALL listing currently active
          prefixNodes = activeNodes.slice(0, -1); // drop the ALL child node; we'll re-add it among siblings
        }
      }
    } else if (leaf && leaf.entityId !== 'ALL') {
      const childTypes = this.getDirectChildTypes(leaf.contentTypeId);
      if (childTypes.length > 0) {
        branchParent = leaf;
        branchChildTypes = childTypes;
        selectedChildFromUrl = null;
        prefixNodes = activeNodes;
      }
    }

    const menuLevels: HierarchyMenuLevel[] = [];
    let menuIndex = 0;

    const pushLevel = (params: {
      depth: number;
      contentTypeId: string;
      parentSelectedId: string | null;
      selectedId: string;
      routeNodes: ContentPathNode[];
    }) => {
      const loading = this.entitiesLoadingByType.has(this.entitiesKey(params.contentTypeId));
      const entities = this.entitiesByType.get(this.entitiesKey(params.contentTypeId)) || [];
      menuLevels.push({
        menuIndex,
        depth: params.depth,
        contentTypeId: params.contentTypeId,
        parentSelectedId: params.parentSelectedId,
        selectedId: params.selectedId,
        routeNodes: params.routeNodes,
        expanded: this.expandedHierarchyIndex === menuIndex,
        entities,
        loading
      });
      this.ensureEntitiesLoaded(params.contentTypeId);
      menuIndex += 1;
    };

    // Prefix chain (selected nodes from URL, up to the branching parent).
    for (let depth = 0; depth < prefixNodes.length; depth += 1) {
      const node = prefixNodes[depth];
      const parentSelectedId =
        depth === 0 ? null : prefixNodes[depth - 1].entityId === 'ALL' ? null : prefixNodes[depth - 1].entityId;
      if (depth > 0 && !parentSelectedId) {
        break; // spec: when parent is ALL, no child level is shown
      }

      pushLevel({
        depth,
        contentTypeId: node.contentTypeId,
        parentSelectedId,
        selectedId: node.entityId,
        routeNodes: prefixNodes.slice(0, depth + 1)
      });
    }

    // Last menu depth: render all direct child types (siblings) of the branchParent.
    if (branchParent) {
      if (branchChildTypes.length > 0) {
        const childDepth = prefixNodes.length;
        const parentSelectedId = branchParent.entityId !== 'ALL' ? branchParent.entityId : null;
        if (parentSelectedId) {
          for (const childTypeId of branchChildTypes) {
            const selectedId =
              selectedChildFromUrl && selectedChildFromUrl.contentTypeId === childTypeId
                ? selectedChildFromUrl.entityId
                : 'ALL';

            pushLevel({
              depth: childDepth,
              contentTypeId: childTypeId,
              parentSelectedId,
              selectedId,
              routeNodes: [...prefixNodes, { contentTypeId: childTypeId, entityId: selectedId }]
            });
          }
        }
      }
    }

    this.hierarchyMenu = menuLevels;
  }

  private entitiesKey(contentTypeId: string): string {
    const lang = this.activeLanguage();
    return `${lang}|${contentTypeId}`;
  }

  private ensureEntitiesLoaded(contentTypeId: string): void {
    if (!this.projectId || isSystemContentType(contentTypeId)) {
      return;
    }

    const key = this.entitiesKey(contentTypeId);
    if (this.entitiesByType.has(key) || this.entitiesLoadingByType.has(key)) {
      return;
    }

    this.entitiesLoadingByType.add(key);
    const lang = this.activeLanguage();
    this.subscriptions.add(
      this.structureService.getProjectStructureEntities(this.projectId, contentTypeId, lang).subscribe({
        next: (response) => {
          this.entitiesByType.set(key, response.entities || []);
          this.entitiesLoadingByType.delete(key);
          // Refresh menu with loaded entities.
          this.rebuildHierarchyMenu(this.lastNodes);
        },
        error: () => {
          this.entitiesByType.set(key, []);
          this.entitiesLoadingByType.delete(key);
        }
      })
    );
  }

  private activeLanguage(): string {
    return normalizeLanguageTag(this.translocoSrv.getActiveLang(), 'es');
  }

  private buildRouteFromNodes(nodes: ContentPathNode[]): string {
    if (!nodes || nodes.length === 0) {
      return '/charts';
    }

    const segments: string[] = [];
    for (let i = 0; i < nodes.length; i += 1) {
      const node = nodes[i];
      if (!node.contentTypeId) {
        continue;
      }

      const isRoot = i === 0;
      const isLast = i === nodes.length - 1;
      const isAll = node.entityId === 'ALL';

      if (isRoot) {
        // Root uses /<type> for ALL, /<type>/<id> for specific.
        segments.push(node.contentTypeId);
        if (!isAll) {
          segments.push(node.entityId);
        }
        continue;
      }

      // Nested levels:
      // - ALL is represented by a trailing type segment (and must be last).
      // - Specific selection is /<type>/<id>.
      segments.push(node.contentTypeId);
      if (!isAll) {
        segments.push(node.entityId);
      } else if (!isLast) {
        // If someone built an invalid node list, keep the URL valid by stopping here.
        break;
      }
    }

    return `/${segments.join('/')}`;
  }

  private validateStructureChain(nodes: ContentPathNode[]): string | null {
    if (this.structureLevels.length === 0) {
      return null;
    }

    if (nodes.length > 0 && isSystemContentType(nodes[0].contentTypeId)) {
      return null;
    }

    const existsType = (typeId: string) => this.structureLevels.some((lvl) => lvl.contentTypeId === typeId);
    const isChildOf = (parentTypeId: string, childTypeId: string) =>
      this.structureLevels.some((lvl) => lvl.contentTypeId === childTypeId && lvl.parentTypeId === parentTypeId);

    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      if (!existsType(node.contentTypeId)) {
        return `Tipo de estructura desconocido: '${node.contentTypeId}'.`;
      }
      if (i === 0) {
        continue;
      }
      const parent = nodes[i - 1];
      if (!isChildOf(parent.contentTypeId, node.contentTypeId)) {
        return `Ruta inválida: '${node.contentTypeId}' no es hijo de '${parent.contentTypeId}'.`;
      }
    }

    return null;
  }

  private getDirectChildTypes(parentTypeId: string): string[] {
    const children = this.structureLevels
      .filter((level) => level.parentTypeId === parentTypeId)
      .sort((a, b) => a.level - b.level || a.contentTypeId.localeCompare(b.contentTypeId));
    return [...new Set(children.map((child) => child.contentTypeId))];
  }

  private getSupportedMetricsForContentType(contentTypeId: string): SupportedMetricType[] {
    const preferredOrder: KnownMetricType[] = [
      'pantallasSenderos',
      'menu_interaction',
      'app_startup',
      'screen_view',
      'form_submission',
      'content_feedback',
      'registroUsuarios',
      'puntosControl',
      'beacons',
      'gamificacion'
    ];

    if (isSystemContentType(contentTypeId)) {
      const metrics = [...(SYSTEM_CONTENT_TYPE_METRICS.get(contentTypeId) || [])];
      if (Object.keys(this.currentStructureMetrics).length === 0) {
        return metrics;
      }

      return metrics.filter((metricType) => {
        const metric = this.currentStructureMetrics[metricType];
        return Array.isArray(metric?.validSubtypes) && metric.validSubtypes.length > 0 && !metric.isDerived;
      });
    }

    if (this.structureLevels.length === 0) {
      return preferredOrder;
    }

    const level = this.structureLevels.find((entry) => entry.contentTypeId === contentTypeId);
    const available = level?.availableMetrics || [];

    const nonDerived = available.filter((m) => !this.currentStructureMetrics[m]?.isDerived);

    return nonDerived.sort((a, b) => {
      const idxA = preferredOrder.indexOf(a as KnownMetricType);
      const idxB = preferredOrder.indexOf(b as KnownMetricType);
      return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
    });
  }

  private updateSubtypesForMetric(metricType: SupportedMetricType): void {
    const config = this.currentStructureMetrics[metricType];
    this.subtypesForMetric = config?.validSubtypes || [];
    if (!this.selectedSubtype || !this.subtypesForMetric.includes(this.selectedSubtype)) {
      this.selectedSubtype = this.subtypesForMetric[0] || null;
    }
  }

  private extractTopLevelTypes(levels: ProjectStructureLevel[]): string[] {
    const ordered = [...levels].sort((a, b) => a.level - b.level);
    const minLevel = ordered[0]?.level;
    if (typeof minLevel !== 'number') {
      return [];
    }
    return ordered
      .filter((lvl) => lvl.level === minLevel)
      .map((lvl) => lvl.contentTypeId)
      .filter((value, index, array) => array.indexOf(value) === index);
  }
}
